import { getPillar } from "../../data/ybf/pillars";
import { SOURCE_META } from "../../data/ybf/stories";
import { MapPin, Mic, Handshake } from "lucide-react";
import VoiceListenButton from "./VoiceListenButton";

const ICONS = { Mic };

export function StoryChips({ story, dense = false, showListen = true }) {
  const p = getPillar(story.pillar);
  const src = SOURCE_META[story.source];
  const SrcIcon = src ? ICONS[src.icon] : null;
  const size = dense ? "text-[9px] px-1.5 py-0.5" : "text-[10px] px-2 py-0.5";

  return (
    <div className="flex flex-wrap items-center gap-1">
      {/* Pillar chip */}
      <span
        className={`inline-flex items-center font-bold rounded-full ${size}`}
        style={{ background: `${p?.color}15`, color: p?.color }}
        data-testid={`story-chip-pillar-${story.id}`}
      >
        {p?.short}
      </span>
      {/* Source chip */}
      {src && (
        <span
          className={`inline-flex items-center gap-1 font-semibold rounded-full ${size} ${src.bg} ${src.text}`}
          data-testid={`story-chip-source-${story.id}`}
        >
          {SrcIcon && <SrcIcon size={dense ? 8 : 10} />}
          {story.source}
        </span>
      )}
      {/* Partner chip */}
      <span
        className={`inline-flex items-center gap-1 font-medium rounded-full bg-slate-100 text-slate-700 ${size}`}
        data-testid={`story-chip-partner-${story.id}`}
        title={story.partner}
      >
        <Handshake size={dense ? 8 : 10} />
        <span className="truncate max-w-[200px]">{story.partner}</span>
      </span>
      {/* Listen button — only for Voice AI sourced stories */}
      {showListen && story.source === "Voice AI" && (
        <VoiceListenButton text={story.quote} dense={dense} />
      )}
    </div>
  );
}

export default function StoryCard({ story, variant = "full" }) {
  const p = getPillar(story.pillar);

  if (variant === "compact") {
    return (
      <div
        data-testid={`story-card-${story.id}`}
        className="bg-white rounded-xl border border-slate-200 p-3 hover:shadow-md transition-shadow"
      >
        <div className="flex items-start gap-2.5">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-[11px] flex-shrink-0"
            style={{ background: p?.color }}
          >
            {story.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div>
                <div className="text-xs font-bold text-slate-900 truncate">{story.name}</div>
                <div className="text-[10px] text-slate-500 flex items-center gap-1">
                  <MapPin size={9} /> {story.location}
                </div>
              </div>
            </div>
            <StoryChips story={story} dense />
            <p className="text-[11px] text-slate-700 italic mt-2 leading-snug line-clamp-2">
              "{story.quote}"
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Full variant
  return (
    <div
      data-testid={`story-${story.id}`}
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
    >
      <div className="h-1.5" style={{ background: p?.color }} />
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0"
            style={{ background: p?.color }}
          >
            {story.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-slate-900">{story.name}</h3>
            <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
              <MapPin size={10} /> {story.location}
            </div>
          </div>
        </div>

        {/* Chips */}
        <div className="mt-3">
          <StoryChips story={story} />
        </div>

        <h4 className="text-sm font-bold text-slate-800 mt-4 leading-snug">{story.headline}</h4>

        <blockquote
          className="mt-3 pl-3 border-l-4 italic text-sm text-slate-700 leading-relaxed"
          style={{ borderColor: p?.color }}
        >
          "{story.quote}"
        </blockquote>

        <div
          className="mt-4 text-xs font-semibold px-3 py-2 rounded-lg"
          style={{ background: `${p?.color}10`, color: p?.color }}
        >
          Impact: {story.impact}
        </div>
      </div>
    </div>
  );
}
